import type { FeedLotState } from '@auctisync/engine-wire';
import type { FeedLot, YouStanding } from './engine-feed.js';
import { secondsUntil } from './live-clock.js';
import type { PublicLotRow } from './lots.js';
import { isLotStillOpen } from './watchlist.js';

/**
 * The row every live page renders a lot from — ONE type, ONE mapping, for the
 * website and the app (2026-10-08).
 *
 * Until now each surface mapped its own: the app in
 * `apps/bidder/src/live-room/toLiveLot.ts`, the website inline in
 * `PublicLiveRoom`'s `useMemo`. The two had drifted on six questions (a lot's
 * name when its title is blank, which close time counts, whether a lot that
 * has not started — or was cancelled — has "ended", how a part-second rounds,
 * what "no close time" reads as, and which fields exist at all) — so the same
 * lot could say two things depending on the device. `live-lot.test.ts` pins
 * this mapping against verbatim copies of both, and names each place it
 * picked one side and why.
 *
 * The mapping takes the CATALOGUE row the page already read from Supabase
 * (title, number, opening bid, condition — the lot's description) and, on a
 * sale the engine runs, the engine's live view of it (price, bids, clock,
 * state, reserve). The engine wins every field it carries; the row supplies
 * the rest. On a Supabase-book sale there is no live view and the row is the
 * whole answer — the same function, so the two books render alike.
 *
 * It decides no money: the next minimum is the ENGINE's (`nextMin`), never
 * re-derived here.
 */
export interface LiveLot {
  id: string;
  lotNumber: number;
  title: string;
  /**
   * The current price, in cents — the opening bid when there is none. Read
   * `priceHidden` before showing it as a bid: a house can hide amounts.
   */
  current: number;
  /**
   * The house hides this lot's price from this viewer, or the feed cannot rule
   * out that it does (the price is null and bids may exist). `current` then
   * holds the opening bid only so older renderers keep a number; it is NOT
   * the price. Show a dash. A masked amount is null, never 0 — or the opening
   * bid (memory: reference_auction_visibility_flags).
   */
  priceHidden: boolean;
  bids: number;
  /** The house hides the bid count from this viewer; `bids` is then 0 and means nothing. */
  bidsHidden: boolean;
  /** Seconds to close on the clock given — `secondsUntil`: rounded up, ≥ 0, Infinity when none. */
  secs: number;
  /** The close time a countdown re-derives from as it ticks. */
  endsAt: string | null;
  /** The lot's status in the catalogue's words (`lots.status`), whichever book it is in. */
  status: string;
  /** Finished: sold, unsold, no sale or cancelled — `!isLotStillOpen`. */
  ended: boolean;
  reserveMet: boolean;
  /** `has_reserve === false`. Null (unknown) is not "no reserve" — the badge is a promise. */
  noReserve: boolean;
  hasReserve: boolean | null;
  /** The viewer's standing; null when signed out or not bidding. */
  you: 'winning' | 'outbid' | null;
  category: string;
  photo: string | null;
  watched: boolean;
  /** The opening bid, for Focused Watch's "Opened at". */
  openedAt: number;
  condition: string | null;
  /** Inventory specs; not on the lot row, so null from a live page (see the web layout's note). */
  specs: Record<string, unknown> | null;
  /** The engine's minimum next bid; null when not known (a Supabase-book sale, or a summary line). */
  nextMin: number | null;
}

/** The catalogue fields the mapping reads. Any `PublicLotRow` fits. */
export type LiveLotRow = Pick<
  PublicLotRow,
  | 'id'
  | 'lot_number'
  | 'title'
  | 'current_bid_cents'
  | 'starting_bid_cents'
  | 'bid_count'
  | 'ends_at'
  | 'scheduled_ends_at'
  | 'status'
  | 'reserve_met'
  | 'has_reserve'
  | 'condition'
>;

export interface LiveLotOptions {
  /** Server-corrected now (`LiveClock.nowMs()`), injected so a frozen clock gives the same numbers. */
  nowMs: number;
  /** The engine's view of the lot, on a sale the engine runs (`feed.lot(id)`). */
  live?: FeedLot | null | undefined;
  /**
   * The engine's account of the viewer's standing (`feed.you(id)`). A
   * standing given here is the answer; `null` says "none"; undefined defers
   * to `standing`.
   */
  you?: YouStanding | null | undefined;
  /** The viewer's standing from `resolveStanding`, used when there is no `you`. */
  standing?: 'winning' | 'outbid' | null | undefined;
  watched?: boolean | undefined;
  photo?: string | null | undefined;
  categoryName?: string | null | undefined;
}

/**
 * The engine's lot state in the catalogue's words — the same words the close
 * projection writes (20261007170000): a lot with no winner and a lot the
 * house's own bidder won are both `unsold`; a Buy It Now sale is `sold`.
 * `bin_pending` (a Buy It Now being confirmed) reads `closing`: still showing,
 * not biddable, not finished — it can return to `open`.
 */
const STATUS_OF: Record<FeedLotState, string> = {
  pending: 'scheduled',
  open: 'active',
  bin_pending: 'closing',
  closing: 'closing',
  closed_sold: 'sold',
  closed_bin_sold: 'sold',
  closed_no_winner: 'unsold',
  closed_no_sale: 'unsold',
  canceled: 'cancelled',
};

export function feedStateToLotStatus(state: FeedLotState): string {
  return STATUS_OF[state];
}

/** The viewer's standing from the engine's `you`. */
export function standingFromYou(you: YouStanding | null | undefined): 'winning' | 'outbid' | null {
  if (you === null || you === undefined) return null;
  if (you.leading) return 'winning';
  // A `you` exists for a lot the bidder holds a ceiling on; not leading it is
  // being outbid (or, once closed, having lost it — the same word the
  // catalogue path uses for both).
  return you.ceiling_cents !== null || you.outbid ? 'outbid' : null;
}

/**
 * `lots.title` is nullable. An untitled lot must still be identifiable:
 * "Lot 14" beats a blank tile. Moved here from the app (2026-09-21 it became
 * the app's one name for an untitled lot); the website printed a blank or
 * whitespace title as it stood.
 */
export function lotDisplayTitle(row: Pick<PublicLotRow, 'title' | 'lot_number'>): string {
  const title = row.title?.trim();
  if (title) return title;
  return row.lot_number !== null && row.lot_number !== undefined
    ? `Lot ${String(row.lot_number)}`
    : 'Untitled lot';
}

/**
 * Whether a price is one the house hides from this viewer — or one the page
 * cannot rule out that it hides: no price, and bids exist (or their count is
 * hidden too). A lot with no bids and no price simply has none yet. THE rule
 * behind `LiveLot.priceHidden`, exported for surfaces that render a row
 * rather than a `LiveLot` (a watchlist card, My bids).
 */
export function priceIsHidden(priceCents: number | null, bidCount: number | null): boolean {
  return priceCents === null && (bidCount === null || bidCount > 0);
}

/** Whether the bid count is hidden from this viewer — `LiveLot.bidsHidden`. */
export function bidsAreHidden(bidCount: number | null): boolean {
  return bidCount === null;
}

export function toLiveLot(row: LiveLotRow, opts: LiveLotOptions): LiveLot {
  const live = opts.live ?? null;

  const priceCents = live !== null ? live.current_high_cents : row.current_bid_cents;
  const bidCount = live !== null ? live.bid_count : row.bid_count;
  // The engine's clock moves with every extension; the row's is the plan.
  const endsAt = live?.close_time ?? row.ends_at ?? row.scheduled_ends_at;
  const status = live !== null ? feedStateToLotStatus(live.state) : row.status;
  // A summary line does not carry the reserve: the row answers until the full view does.
  const reserveMet =
    live !== null && live.reserve_met !== undefined ? live.reserve_met : row.reserve_met;

  return {
    id: row.id,
    lotNumber: row.lot_number,
    title: lotDisplayTitle(row),
    current: priceCents ?? row.starting_bid_cents,
    priceHidden: priceIsHidden(priceCents, bidCount),
    bids: bidCount ?? 0,
    bidsHidden: bidsAreHidden(bidCount),
    secs: secondsUntil(endsAt, opts.nowMs),
    endsAt,
    status,
    ended: !isLotStillOpen({ status }),
    reserveMet: reserveMet === true,
    noReserve: row.has_reserve === false,
    hasReserve: row.has_reserve,
    you: opts.you !== undefined ? standingFromYou(opts.you) : (opts.standing ?? null),
    category: opts.categoryName ?? '',
    photo: opts.photo ?? null,
    watched: opts.watched ?? false,
    openedAt: row.starting_bid_cents,
    condition: row.condition,
    specs: null,
    nextMin: live?.next_min_cents ?? null,
  };
}
